import { SensorDataGraph } from '../model/sensorData';
import { ArrayUtils } from './arrayUtils';
import { DateUtils } from './dateUtils';

export interface SensorSeries {
  x: Date[];
  y: number[];
}

export class SensorGraphUtils {

  /**
   * Puntos de un sensor para Plotly, con X e Y emparejados fila a fila.
   *
   * QLOD-77: antes X eran las fechas de TODAS las filas del datasource e Y solo los valores
   * de este sensor. Cuando los sensores no comparten marcas de tiempo (o en el dialogo de
   * eventos, que tiene una fila por sensor y fecha), las longitudes no coinciden y Plotly
   * pinta cada valor en la fecha de otra fila: la curva aparece desplazada respecto al dato
   * primario.
   */
  static seriesOf(datasource: SensorDataGraph[], field: string): SensorSeries {
    const x: Date[] = [];
    const y: number[] = [];

    (datasource ?? []).forEach(row => {
      const value = row[field];

      if (value !== undefined && value !== null) {
        x.push(row.date);
        y.push(Number(value));
      }
    });

    return { x, y };
  }

  /**
   * Primera y ultima marca de tiempo de una serie. `Math.min(...array)` revienta la pila con
   * series de decenas de miles de puntos; ArrayUtils.min/max no. Las filas sin fecha se ignoran
   * (new Date(null) es 1970 y se llevaria el minimo).
   */
  static timeBounds(datasource: SensorDataGraph[]): { min: number, max: number } {
    const times = (datasource ?? [])
      .filter(row => row.date != null)
      .map(row => new Date(row.date).getTime())
      .filter(t => !isNaN(t));

    return { min: ArrayUtils.min(times), max: ArrayUtils.max(times) };
  }

  /** Fecha en el formato de los campos de texto de los dialogos; null si no es valida. */
  static parseFieldDate(text: string): Date {
    if (text == null || text.trim() === '') {
      return null;
    }

    const date = DateUtils.strTimeToDate(text.trim());

    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * La fecha que representa un campo de texto. Si el texto sigue siendo el de `current` (no se ha
   * tocado), devuelve `current` tal cual para no perder los milisegundos en el ida y vuelta por
   * el formato; si no, lo parsea.
   */
  static resolveFieldDate(text: string, current: Date): Date {
    if (current != null && text != null && text.trim() === SensorGraphUtils.formatFieldDate(current)) {
      return current;
    }

    return SensorGraphUtils.parseFieldDate(text);
  }

  /**
   * Rango que se envia al copiar desde otro ensayo (QLOD-77): el de los campos de texto, que es
   * lo que ve el usuario, no la seleccion de la grafica. `startRange`/`endRange` son las fechas
   * exactas de la seleccion, para no perder milisegundos si el texto no se ha tocado.
   */
  static resolveCopyRange(startText: string, endText: string, startRange: Date, endRange: Date,
    sourceData: { date: Date }[]): { dateFrom: Date, dateTo: Date, errorKeys: string[] } {
    const dateFrom = SensorGraphUtils.resolveFieldDate(startText, startRange);
    const dateTo = SensorGraphUtils.resolveFieldDate(endText, endRange);
    const errorKeys: string[] = [];

    if (dateFrom == null) {
      errorKeys.push('executionEdit.copyEssay.form.error.startDate');
    }
    if (dateTo == null) {
      errorKeys.push('executionEdit.copyEssay.form.error.endDate');
    }
    if (dateFrom != null && dateTo != null) {
      const from = dateFrom.getTime();
      const to = dateTo.getTime();

      if (from >= to) {
        errorKeys.push('executionEdit.copyEssay.form.error.range');
      } else if (!(sourceData ?? []).some(d => d.date != null && d.date.getTime() >= from && d.date.getTime() <= to)) {
        // Una fecha tecleada fuera del ensayo origen no copiaria nada y diria "copiado".
        errorKeys.push('executionEdit.copyEssay.form.error.noData');
      }
    }

    return { dateFrom, dateTo, errorKeys };
  }

  static formatFieldDate(date: Date): string {
    return date == null ? null : DateUtils.toDateTimeStr(date);
  }
}
